import React from "react";
import { Tooltip } from "antd";
import { Link } from "react-router-dom";

import { EntityRefKind, formatEntityRef } from "../../lib/entityRef";
import styles from "./EntityRefBadge.module.scss";

// Shared renderer for human-facing entity references — PLATFORM STANDARD
// (owner 2026-07-21). One component so every entity (PJ / TR / AL / AR /
// RL / CH) shows the same format and behavior:
//
//   * Visible text is the short ref ("SP-0834E", "TR-52A91"); hover reveals
//     the full canonical id. Canonical ids stay canonical everywhere else
//     (APIs, audits, URLs, Technical Details).
//   * NO copy icon next to the ref (owner: an unlabeled copy-style icon
//     makes the action unclear). Canonical-id copy lives behind a clearly
//     labeled action on detail pages.
//   * When a destination is provided the ref is a REAL link with its own
//     click target: clicking it always opens the destination, never toggles
//     row selection (click/keyboard events do not propagate to the row), and
//     Enter/Space both activate it.

export function EntityRefBadge({
  kind,
  id,
  refLabel,
  to,
  onOpen,
  prefix,
  muted = false,
  testId,
}: {
  kind: EntityRefKind;
  id: string | null | undefined;
  /** Collision-safe ref from buildEntityRefs; defaults to the standard ref. */
  refLabel?: string;
  /** Route destination — renders the ref as a real link. */
  to?: string;
  /** Programmatic open (modals that must close before navigating). */
  onOpen?: () => void;
  /** Optional leading text, e.g. "Job". */
  prefix?: string;
  /** Fallback styling for unresolved-name contexts. */
  muted?: boolean;
  testId?: string;
}): React.ReactElement {
  if (!id) return <span>—</span>;
  const label = refLabel || formatEntityRef(kind, id);

  const stop = (event: React.SyntheticEvent) => {
    // The ref's click target belongs to the ref alone — never let the row
    // (checkbox selection, row handlers) receive the event.
    event.stopPropagation();
  };

  let core: React.ReactElement;
  if (to) {
    core = (
      <Link
        to={to}
        className={styles.link}
        data-testid={testId}
        onClick={stop}
        onKeyDown={(event) => {
          stop(event);
          // Anchors activate on Enter natively; Space must work too.
          if (event.key === " ") {
            event.preventDefault();
            event.currentTarget.click();
          }
        }}
      >
        {label}
      </Link>
    );
  } else if (onOpen) {
    core = (
      <span
        role="link"
        tabIndex={0}
        className={styles.link}
        data-testid={testId}
        onClick={(event) => {
          stop(event);
          onOpen();
        }}
        onKeyDown={(event) => {
          stop(event);
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpen();
          }
        }}
      >
        {label}
      </span>
    );
  } else {
    core = (
      <span className={muted ? styles.muted : styles.ref} data-testid={testId}>
        {label}
      </span>
    );
  }

  return (
    <span style={{ whiteSpace: "nowrap" }}>
      {prefix ? `${prefix} ` : null}
      <Tooltip title={id} placement="top">
        {core}
      </Tooltip>
    </span>
  );
}
